import { TranscriptMessageReadReceipt } from './api';
import { InlineConfig } from './external';
import { ProactiveActionPayload } from './actions';

export interface Banner {
    banner: string;
    url: string;
}
export interface Link {
    text: string;
    url: string;
    icon: string;
}
export interface VisitorConfig {
    firstName?: string | void;
    lastName?: string | void;
    email?: string | void;
    phone?: string | void;
}

export interface LocationState {
    id: number;
    name: string;
    avatar: string;
    phone: string | void;
    customerId: number;
    offlineMessage: string | void;
    teamIds: Array<number>;
}

interface BaseSettings {
    browser: { webRtc: boolean };
    inlineConfig: InlineConfig;
    ctaText: string;
    botId: number;
    bootstrapped: boolean;
    settingsFetched: boolean;
    smsPhone: string | void;
    locale: string;
    banners: Array<Banner>;
    starters: Array<string>;
    links: Array<Link>;
    requireVisitorFields: boolean;
    requiredFields: Array<string>;
    videoEnabled: boolean;
    maxWaitSeconds: number;
    bypassCustomizePanel: boolean;
    offlineOption: string;
    pusherKey: string;
    messageListType: string;
    openLinksExternally: boolean;
    hideBanner?: boolean;
    hideCta?: boolean;
    customMetadata?: string;
    ctaBottomMargin: string;
    extraContext?: { [key: string]: any };
    zIndex: string;
}

export interface MobileCtaSettings extends BaseSettings {
    ctaType: 'mobile';
    ctaBackgroundColor: string;
    ctaForegroundColor: string;
}

export interface TabCtaSettings extends BaseSettings {
    ctaType: 'tab';
    ctaPosition: 'bottom right' | 'bottom left' | 'left' | 'right';
    ctaBackgroundColor: string;
    ctaForegroundColor: string;
    ctaText: string;
}

export interface CircleWithTailCtaSettings extends BaseSettings {
    ctaType: 'circle with tail';
    ctaPosition: 'bottom right' | 'bottom left';
    ctaBackgroundColor: string;
    ctaForegroundColor: string;
    ctaCustomImage?: string;
    ctaText: string;
    ctaTailBackgroundColor: string;
    ctaTailForegroundColor: string;
}

export interface CircleCtaSettings extends BaseSettings {
    ctaType: 'circle';
    ctaPosition: 'bottom right' | 'bottom left';
    ctaBackgroundColor: string;
    ctaForegroundColor: string;
    ctaCustomImage?: string;
}

interface CustomCtaSettings extends BaseSettings {
    ctaType: 'custom';
}

export interface ProactiveSettings extends BaseSettings {
    ctaType: string;
    ctaPosition: string;
    ctaBackgroundColor: string;
    ctaForegroundColor: string;
}

export type SettingsState =
    | TabCtaSettings
    | CircleWithTailCtaSettings
    | CircleCtaSettings
    | CustomCtaSettings
    | MobileCtaSettings;

export enum Status {
    online = 'online',
    offline = 'offline',
    away = 'away',
    dnd = 'do not disturb',
}

export interface Team {
    id: number;
    name: string;
    status: Status;
    offlineMessage: string | void;
    phone: string | void;
    userIds: Array<number>;
}
export interface TeamsState {
    [id: number]: Team;
}

export interface User {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    avatar: string;
    isOnline: boolean;
}
export interface UsersState {
    [id: number]: User;
}

export interface RoxanneState {
    visitorId?: number;
}

export interface RootState {
    settings: SettingsState;
    teams: TeamsState;
    users: UsersState;
    location: LocationState;
    roxanne: RoxanneState;
    conversation: ConversationState;
    visitor: VisitorState;
    message: MessageState;
    ui: UIState;
    video: VideoState;
}

export interface VisitorState {
    senderId: string;
    sessionStarted: string;
    gaClientId?: string;
    roxanneVisitorId?: string;
    vehicleOfInterest?: object;
    hasConvo: boolean;
    visitorId: number;
    visitorToken: string;
    channel: string;
    declinedProactive?: number;
    contactInfo: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
    };
}

export interface VehicleAttachment {
    doctype: 'vehicle';
    year: string;
    make: string;
    model: string;
    link?: string;
    thumbnail?: string;
    vin?: string;
    stock?: string;
    trim?: string;
    price?: string;
    mpgCity?: string;
    mpgHighway?: string;
    engineDescription?: string;
    transmissionDescription?: string;
    colorExt?: string;
    colorInt?: string;
}

export interface CalculatorAttachment {
    doctype: 'calculator';
    vehiclePrice: number;
    tradeInValue: number;
    downPayment: number;
    loanAmount: number;
    monthlyPayment: number;
    rate: string;
    term: number;
}

export interface BrochureAttachment {
    doctype: 'brochure';
    year: string;
    make: string;
    model: string;
    fileUrl: string;
    thumbUrl: string;
}

export interface AccoladeAttachment {
    doctype: 'accolade';
    year: string;
    make: string;
    model: string;
    accolade: string;
}

export interface IncentiveAttachment {
    doctype: 'incentive';
    year: string;
    make: string;
    model: string;
    title: string;
    incentive: string;
}

export interface VideoAttachment {
    doctype: 'video';
    video_id: string;
    title: string;
    thumbnail: string;
    videoUrl: string;
}

export interface UploadedVideoAttachment {
    doctype: 'uploadedvideo';
    year: string;
    make: string;
    model: string;
    fileUrl: string;
    thumbUrl: string;
    webViewUrl?: string;
}

export interface ImageAttachment {
    doctype: 'image';
    url: string;
}

export interface LinkAttachment {
    doctype: 'link';
    url: string;
}

export type Attachment =
    | CalculatorAttachment
    | VehicleAttachment
    | BrochureAttachment
    | AccoladeAttachment
    | IncentiveAttachment
    | VideoAttachment
    | UploadedVideoAttachment
    | ImageAttachment
    | LinkAttachment;

export interface MessageWithMetadata extends Message {
    avatar?: string;
    readByMe: boolean;
}

export interface Message {
    id: number | string;
    timestamp: string;
    text: string;
    buttons?: string[];
    fromType: string;
    fromId?: number;
    category: string;
    type: string;
    attachments?: Attachment[] | void;
    device?: string;
    readReceipts?: TranscriptMessageReadReceipt[];
    sending?: boolean;
    hasSendError?: boolean;
}

export interface MessageState {
    messages: Message[];
}

export interface ConversationState {
    minimized: boolean;
    hidden: boolean;
    teamId?: number;
    channel?: string;
    starting: boolean;
    startError: boolean;
    active: boolean;
    accepted: boolean;
    endPending: boolean;
    ending: boolean;
    ended: boolean;
    surveyCompleted: boolean;
    userIds: number[];
    typingStatus: {
        active: boolean;
        userId?: number;
        avatar?: string;
    };
}

export interface VideoState {
    request?: {
        timestamp: string;
        peerId: string;
        agentId: number;
        iceServers: any[];
        accepted: boolean;
    };
    localActive?: boolean;
    remoteActive?: boolean;
    remoteStreaming?: boolean;
    remoteClosed?: boolean;
    connectionError?: boolean;
    audioMuted?: boolean;
    videoMuted?: boolean;
}

export interface ConversationWithMetadata extends ConversationState {
    avatar: string;
    users: User[];
}

export interface FormStatus {
    submitting: boolean;
    submitSuccess: boolean;
    submitError: boolean;
    errorMessage?: string;
}

export interface UIState {
    leaveMessageForm: FormStatus;
    proactive?: ProactiveActionPayload;
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/typedefs/store.ts